import { apiClient } from './apiClient';
import {
  Valve,
  ValveCreate,
  ValveUpdate,
  ValveListResponse,
  ValveWithDeviceResponse,
  ValveStatsResponse,
  ValveVolumeCalculation,
} from '@/types/api';

class ValveApiService {
  /**
   * Get all valves
   */
  async getValves(params?: {
    device_id?: number;
    enabled_only?: boolean;
  }): Promise<ValveListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.device_id) {
      searchParams.append('device_id', params.device_id.toString());
    }
    if (params?.enabled_only) {
      searchParams.append('enabled_only', 'true');
    }
    
    const queryString = searchParams.toString();
    const endpoint = `/valves${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<ValveListResponse>(endpoint);
  }

  /**
   * Get valves with device information
   */
  async getValvesWithDeviceInfo(params?: {
    device_id?: number;
    enabled_only?: boolean;
  }): Promise<ValveWithDeviceResponse[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.device_id) {
      searchParams.append('device_id', params.device_id.toString());
    }
    if (params?.enabled_only) {
      searchParams.append('enabled_only', 'true');
    }
    
    const queryString = searchParams.toString();
    const endpoint = `/valves/with-device-info${queryString ? `?${queryString}` : ''}`;
    
    return apiClient.get<ValveWithDeviceResponse[]>(endpoint);
  }

  /**
   * Get a specific valve by ID
   */
  async getValve(valveId: string): Promise<Valve> {
    return apiClient.get<Valve>(`/valves/${valveId}`);
  }

  /**
   * Create a new valve
   */
  async createValve(valveData: ValveCreate): Promise<Valve> {
    return apiClient.post<Valve>('/valves', valveData);
  }

  /**
   * Update a valve
   */
  async updateValve(valveId: string, valveData: ValveUpdate): Promise<Valve> {
    return apiClient.put<Valve>(`/valves/${valveId}`, valveData);
  }

  /**
   * Delete a valve
   */
  async deleteValve(valveId: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/valves/${valveId}`);
  }

  /**
   * Bulk update multiple valves
   */
  async bulkUpdateValves(data: {
    valve_ids: string[];
    enabled?: boolean;
    location?: string;
  }): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/valves/bulk-update', data);
  }

  /**
   * Auto-create valves for a device
   */
  async autoCreateValves(data: {
    device_id: number;
    valve_count?: number;
    name_prefix?: string;
  }): Promise<{ message: string; valves: Valve[] }> {
    return apiClient.post<{ message: string; valves: Valve[] }>('/valves/auto-create', data);
  }

  /**
   * Get valve statistics
   */
  async getValveStats(deviceId?: number): Promise<ValveStatsResponse> {
    const searchParams = new URLSearchParams();
    
    if (deviceId) {
      searchParams.append('device_id', deviceId.toString());
    }
    
    const queryString = searchParams.toString();
    const endpoint = `/valves/stats${queryString ? `?${queryString}` : ''}`;
    
    return apiClient.get<ValveStatsResponse>(endpoint);
  }

  /**
   * Calculate water volume for a valve based on pulse count
   */
  async calculateWaterVolume(valveId: string, pulseCount: number): Promise<ValveVolumeCalculation> {
    const searchParams = new URLSearchParams();
    searchParams.append('pulse_count', pulseCount.toString());
    
    return apiClient.get<ValveVolumeCalculation>(
      `/valves/${valveId}/calculate-volume?${searchParams.toString()}`
    );
  }

  /**
   * Get valves by conversion rate range
   */
  async getValvesByConversionRange(minRate: number, maxRate: number): Promise<{
    valves: Valve[];
    min_rate: number;
    max_rate: number;
    count: number;
  }> {
    const searchParams = new URLSearchParams();
    searchParams.append('min_rate', minRate.toString());
    searchParams.append('max_rate', maxRate.toString());
    
    return apiClient.get<{
      valves: Valve[];
      min_rate: number;
      max_rate: number;
      count: number;
    }>(`/valves/by-conversion-range?${searchParams.toString()}`);
  }

  /**
   * Clean up excess valves from UC51x devices
   */
  async cleanupExcessValves(): Promise<{
    message: string;
    cleaned_valves_count: number;
  }> {
    return apiClient.post<{
      message: string;
      cleaned_valves_count: number;
    }>('/valves/cleanup-excess');
  }
}

// Create and export singleton instance
export const valveApi = new ValveApiService();

// Export class for testing purposes
export { ValveApiService };

export default valveApi;